import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { showHamburgerMenu } from 'ducks/layout';
import { isBookAppointmentSheetOpen, hideBookAppointmentSheet } from 'ducks/sheets';
import { getVpcCode } from 'ducks/vpc';
import Body from 'ui/Typography/Body';
import Box from 'ui/Box';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import { getRange } from 'utils/queryUtils';
import { extractImageSource } from 'utils/imageUtils';
import sheetImage from './Images/IKEA_book_appointment_1x.jpg';
import sheetImageHighres from './Images/IKEA_book_appointment_2x.jpg';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { CopyDesignCodeStateEnum } from '@inter-ikea-kompis/component-copy-design-code';
import { reportBookRemotePlanning } from 'analytics/reporters';
import { KompisHyperlink } from '@inter-ikea-kompis/react-components';
import SheetBox from 'ui/Sheet/SheetBox';
import SheetHeader from 'ui/Sheet/SheetHeader';
import withLoading from 'ui/withLoading/withLoading';
import Sheet from 'ui/Sheet/Sheet';
import { getProductEntities } from 'ducks/core';
import { isMobile } from 'utils/platform';
import { CopyDesignCode } from 'components/CopyDesignCode';
import { useAppDispatch } from 'ducks/hooks';
import { ReportCurrentPageEnum } from 'components/ProductSidebar/HomeDelivery/HomeDelivery';
import { useServiceSettings } from 'contexts/serviceSettings';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const LodingSheetBox = withLoading(SheetBox);

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;
const StyledTopImage = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  padding-bottom: ${({ theme }) => theme.space.xl};
`;

const ButtonWrapper = styled.div`
  width: 100%;
  bottom: 0;
  position: absolute;
  padding: 1.75rem;
  background: white;
`;

export enum BookingCategory {
  BEDROOM = 'bedroom',
  LIVINGROOM = 'livingroom'
}

export const getBookingCategory = () => {
  const range = getRange();
  switch (range) {
    case 'besta':
    case 'eket':
      return BookingCategory.LIVINGROOM;
    default:
      return BookingCategory.BEDROOM;
  }
};

function BookAppointmentSheet() {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const visible = useSelector(isBookAppointmentSheetOpen);
  const vpcCode = useSelector(getVpcCode);
  const productEntitiesInScene = useSelector(getProductEntities);
  const {
    dexfSettings: {
      kompis: {
        urls: { supportBookingLink }
      }
    }
  } = useServiceSettings();

  const [copyDesignCodeState, setCopyDesignCodeState] = useState<CopyDesignCodeStateEnum>(
    CopyDesignCodeStateEnum.default
  );

  const sheetImages = [
    { src: sheetImage, width: 328, height: 384 },
    { src: sheetImageHighres, width: 656, height: 768 }
  ];
  const { fallbackImage } = extractImageSource(sheetImages);

  // Using encodeURIComponent() to fix a security alert
  const bookingPlannerUrl = supportBookingLink.replace(
    `{vpcCode}`,
    encodeURIComponent(vpcCode || '')
  );

  const onClose = () => {
    dispatch(hideBookAppointmentSheet());
  };

  const goToUrl = () => {
    reportBookRemotePlanning({
      currentMenu: ReportCurrentPageEnum.bookAppointmentSheet
    });
    window.open(bookingPlannerUrl, '_blank');
  };

  const goBackAndShowHamburgerMenu = () => {
    dispatch(hideBookAppointmentSheet());
    setTimeout(() => dispatch(showHamburgerMenu()), 10);
  };

  return (
    <Sheet
      onOverlayClick={() => dispatch(hideBookAppointmentSheet())}
      visible={visible}
      data-testid="book-appointment-sheet"
    >
      <SheetHeader
        backIcon={true}
        onCloseButtonClick={onClose}
        onBackButtonClick={goBackAndShowHamburgerMenu}
      />
      <LodingSheetBox isLoading={productEntitiesInScene.length > 0 && !vpcCode}>
        {!isMobile() && (
          <StyledTopImage
            src={fallbackImage.src}
            alt={formatMessage({ id: 'storageone.bookAppointmentButtonText' })}
          />
        )}
        <ContentWrapper>
          <Box>
            <Title lineHeight="tall" pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
              <KompisHyperlink
                theme={SkapaTheme}
                text={formatMessage({ id: 'storageone.bookAppointmentTitle' })}
                onLinkClick={goToUrl}
              ></KompisHyperlink>
            </Title>
            <Body lineHeight="tall" type={ThemeFontStyleTypeEnum.bodyMedium}>
              <KompisHyperlink
                theme={SkapaTheme}
                text={formatMessage({ id: 'storageone.bookAppointmentText' })}
                onLinkClick={goToUrl}
              ></KompisHyperlink>
            </Body>
            <CopyDesignCode
              copyDesignCodeState={copyDesignCodeState}
              setCopyDesignCodeState={setCopyDesignCodeState}
              data-testid={'copy-design-code'}
            />
          </Box>
        </ContentWrapper>
      </LodingSheetBox>
      <ButtonWrapper>
        <Button
          mt="md"
          type={ButtonTypeEnum.primary}
          text={formatMessage({ id: 'storageone.bookAPlanningSession' })}
          onClick={goToUrl}
          width={'100%'}
        />
      </ButtonWrapper>
    </Sheet>
  );
}

export default BookAppointmentSheet;
